import { useSyncExternalStore } from 'react';

/**
 * Light or dark, as one fact two very distant parts of the tree both need.
 *
 * The template's palette is CSS — a second `@theme` block under `[data-theme="dark"]` — so
 * everything written in template classes switches by itself. Ant Design's is not: `Table`,
 * `Select` and `Modal` paint their own surfaces from a `ThemeConfig` resolved in JavaScript, and
 * with no algorithm chosen they stay light. A dark page with a white table in the middle of it is
 * the result, and it is worse than either theme alone.
 *
 * So `ConfigProvider` has to see the mode. It wraps the whole app, above the sign-in screen and
 * outside the shell, while the control that changes the mode is a button in the topbar — as far
 * apart as two things in this tree can be. An external store beats both alternatives: lifting the
 * state to `App` means threading it through every screen, and a context means a provider whose
 * only consumer is two components.
 *
 * The attribute on `<html>` is the source of truth rather than a mirror of this store, because a
 * pre-paint script in `index.html` has already set it before any of this exists — see the comment
 * there. This module writes the attribute and reads it back; nothing holds a second copy.
 */

export type ThemeMode = 'light' | 'dark';

/** Shared with the pre-paint script in `index.html`. */
const STORAGE_KEY = 'attirerent.theme';

const listeners = new Set<() => void>();

function read(): ThemeMode {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

export function setThemeMode(mode: ThemeMode): void {
  if (read() === mode) {
    return;
  }

  document.documentElement.setAttribute('data-theme', mode);

  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Private mode, or storage disabled. The choice still applies to this sitting; it simply does
    // not survive a reload, which is a better outcome than refusing to switch at all.
  }

  for (const listener of listeners) {
    listener();
  }
}

export function toggleThemeMode(): void {
  setThemeMode(read() === 'dark' ? 'light' : 'dark');
}

export function useThemeMode(): ThemeMode {
  // Same function for both snapshots: there is no server render here, and `read()` is a DOM read
  // that is stable between notifications, which is what `useSyncExternalStore` requires.
  return useSyncExternalStore(subscribe, read, read);
}
